import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  robots?: string;
  url?: string;
  image?: string;
  type?: string;
  author?: string;
  twitterCard?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly defaultTitle = 'pakmatti.com - Real Estate Portal in Pakistan';
  private readonly defaultDescription = 'Buy, sell and rent properties in Pakistan. Find the best real estate deals on pakmatti.com.';
  private readonly defaultImage = 'https://pakmatti.com/assets/images/logo.png';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /**
   * Updates all SEO-related metadata for the current page.
   * @param config SEO configuration object
   */
  updateMetadata(config: SeoConfig = {}) {
    const title = config.title ? `${config.title} | pakmatti.com` : this.defaultTitle;
    const description = config.description || this.defaultDescription;
    const url = config.url || this.document.URL;
    const image = config.image || this.defaultImage;
    const robots = config.robots || 'index, follow';
    const type = config.type || 'website';

    this.titleService.setTitle(title);

    // Standard Meta Tags
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'keywords', content: config.keywords || 'real estate, pakistan, property, buy, sell, rent' });
    this.metaService.updateTag({ name: 'robots', content: robots });
    this.metaService.updateTag({ name: 'author', content: config.author || 'pakmatti.com' });
    this.metaService.updateTag({ name: 'language', content: 'English' });
    this.metaService.updateTag({ name: 'theme-color', content: '#274abb' });

    // Open Graph Tags
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:type', content: type });
    this.metaService.updateTag({ property: 'og:image', content: image });
    this.metaService.updateTag({ property: 'og:site_name', content: 'pakmatti.com' });

    // Twitter Tags
    this.metaService.updateTag({ name: 'twitter:card', content: config.twitterCard || 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    this.metaService.updateTag({ name: 'twitter:image', content: image });
    this.metaService.updateTag({ name: 'twitter:site', content: '@mattipk' });

    this.setCanonicalURL(url);
  }

  /**
   * Sets or updates the canonical link tag.
   * @param url Canonical URL
   */
  private setCanonicalURL(url: string) {
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
